import { useMemo } from 'react';
import { useTripsAndJobsSearchQuery } from './useTripsAndJobsSearchQuery';
import { useOperationsSearchQuery } from './useOperationsSearchQuery';
import { useVesselsSearchQuery } from './useVesselsSearchQuery';
import { type SearchResultsManager as SearchManager } from '../types';
import { minSearchPhraseLength } from '../constants';
import { type TargetVessel } from '../../../types';

export const useSearchManager = (
  operation: string,
  searchPhrase: string,
  targetVessel: TargetVessel
): SearchManager => {
  const isSearchPhraseProvided = useMemo(() => {
    return !!searchPhrase && searchPhrase !== '' && searchPhrase.length >= minSearchPhraseLength;
  }, [searchPhrase]);

  const trips = useTripsAndJobsSearchQuery(searchPhrase, isSearchPhraseProvided, operation, targetVessel);

  const operations = useOperationsSearchQuery(searchPhrase, isSearchPhraseProvided, operation, targetVessel);

  const vessels = useVesselsSearchQuery(searchPhrase, isSearchPhraseProvided, operation, targetVessel);

  const hasAnyResults = useMemo(
    () =>
      (!vessels?.isLoading && vessels?.data?.length > 0) ||
      (!operations?.isLoading && operations?.data?.length > 0) ||
      (!trips?.isLoading && trips?.data?.length > 0),
    [vessels?.data, operations?.data, trips?.data]
  );

  return {
    hasAnyResults,
    canSearch: isSearchPhraseProvided,
    isJobsAndTripsLoading: trips?.isLoading ?? true,
    isOperationsLoading: operations?.isLoading ?? true,
    isVesselsLoading: vessels?.isLoading ?? true,
    vesselResults: vessels?.data ?? [],
    operationResults: operations?.data ?? [],
    jobsAndTripsResults: trips?.data ?? [],
  };
};
